import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Search, Edit, Trash2, Plus, Loader2, Users, Clock,
  CheckCircle, XCircle, Eye, Mail, Phone, Globe, MapPin,
  EyeOff, RotateCcw, ShieldCheck, ShieldOff, LogIn, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_BG_IMAGE = "https://customer-assets.emergentagent.com/job_biz-directory-27/artifacts/miycx0rf_614shoplocal-background.png";

const formatLastUpdated = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const BusinessManagement = ({ businesses, token, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [deleteModal, setDeleteModal] = useState({ open: false, business: null });
  const [deleting, setDeleting] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const navigate = useNavigate();
  const businessIdentifier = (business) => encodeURIComponent(business.id || business.business_email);

  const handleEdit = (business) => navigate(`/admin/business/${businessIdentifier(business)}`);

  const handleLoginAsBusiness = async (business) => {
    try {
      const response = await axios.post(`${API}/admin/auth/login-as`, {
        email: business.business_email,
        account_id: business.id,
        user_type: "business",
      }, { headers: { Authorization: `Bearer ${token}` } });
      localStorage.setItem("bizkeep_token", response.data.access_token);
      localStorage.setItem("bizkeep_email", response.data.email);
      localStorage.setItem("bizkeep_user_type", "business");
      toast.success(`Logged in as ${business.business_name || business.business_email}`);
      window.open("/dashboard", "_blank", "noopener,noreferrer");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to login as business");
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.business) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/business/${businessIdentifier(deleteModal.business)}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Business deleted successfully");
      setDeleteModal({ open: false, business: null });
      onRefresh();
    } catch { toast.error("Failed to delete business"); }
    finally { setDeleting(false); }
  };

  const handleStatusChange = async (business, newStatus) => {
    setUpdatingStatus(business.id);
    try {
      await axios.put(`${API}/admin/business/${businessIdentifier(business)}`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Business ${newStatus === "approved" ? "approved" : "rejected"} successfully`);
      onRefresh();
    } catch { toast.error("Failed to update status"); }
    finally { setUpdatingStatus(null); }
  };

  const handleDelistToggle = async (business) => {
    setUpdatingStatus(business.id);
    const newDelistedStatus = !business.is_delisted;
    try {
      await axios.put(`${API}/admin/business/${businessIdentifier(business)}`, { is_delisted: newDelistedStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newDelistedStatus ? "Business delisted from directory" : "Business relisted to directory");
      onRefresh();
    } catch { toast.error("Failed to update listing status"); }
    finally { setUpdatingStatus(null); }
  };

  const handleToggleOverride = async (business) => {
    setUpdatingStatus(business.id);
    try {
      const r = await axios.post(`${API}/admin/business/${businessIdentifier(business)}/toggle-override`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.admin_subscription_override ? "Subscription override enabled — listing now visible" : "Subscription override removed");
      onRefresh();
    } catch { toast.error("Failed to toggle override"); }
    finally { setUpdatingStatus(null); }
  };

  const filteredBusinesses = businesses.filter((biz) => {
    const locations = Array.isArray(biz.business_locations) ? biz.business_locations : [biz.location].filter(Boolean);
    const matchesSearch =
      biz.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      biz.business_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      locations.some((location) => location.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesTab =
      activeTab === "all" ||
      (activeTab === "pending" && biz.status === "pending") ||
      (activeTab === "approved" && (biz.status === "approved" || !biz.status) && !biz.is_delisted) ||
      (activeTab === "rejected" && biz.status === "rejected") ||
      (activeTab === "delisted" && biz.is_delisted === true) ||
      (activeTab === "no_sub" && !biz.subscription_active && !biz.admin_subscription_override);
    return matchesSearch && matchesTab;
  });

  const pendingCount = businesses.filter(b => b.status === "pending").length;
  const approvedCount = businesses.filter(b => (b.status === "approved" || !b.status) && !b.is_delisted).length;
  const rejectedCount = businesses.filter(b => b.status === "rejected").length;
  const delistedCount = businesses.filter(b => b.is_delisted === true).length;
  const noSubCount = businesses.filter(b => !b.subscription_active && !b.admin_subscription_override).length;

  return (
    <>
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        {[
          { label: "Total", count: businesses.length, icon: Users, bg: "bg-slate-100", text: "text-slate-600" },
          { label: "Pending", count: pendingCount, icon: Clock, bg: "bg-yellow-100", text: "text-yellow-600" },
          { label: "Active", count: approvedCount, icon: CheckCircle, bg: "bg-green-100", text: "text-green-600" },
          { label: "Delisted", count: delistedCount, icon: EyeOff, bg: "bg-orange-100", text: "text-orange-600" },
          { label: "Rejected", count: rejectedCount, icon: XCircle, bg: "bg-red-100", text: "text-red-600" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 ${s.bg} rounded-lg flex items-center justify-center`}>
                <s.icon className={`w-6 h-6 ${s.text}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{s.count}</p>
                <p className="text-sm text-slate-500">{s.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Header Actions */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-2xl font-semibold text-slate-900">Manage Businesses</h1>
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input type="text" placeholder="Search businesses..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 h-10 border-slate-200" data-testid="admin-search-input" />
          </div>
          <Button onClick={() => navigate("/admin/business/new")} className="bg-[#1a1a1a] hover:bg-slate-800 text-white" data-testid="admin-add-business-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Business
          </Button>
        </div>
      </div>

      {/* Business Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="border-b border-slate-100">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start rounded-none bg-transparent border-0 p-0">
              {[
                { value: "all", label: `All (${businesses.length})`, border: "border-slate-900" },
                { value: "pending", label: `Pending (${pendingCount})`, border: "border-yellow-500" },
                { value: "approved", label: `Approved (${approvedCount})`, border: "border-green-500" },
                { value: "no_sub", label: `No Sub (${noSubCount})`, border: "border-blue-500" },
                { value: "rejected", label: `Rejected (${rejectedCount})`, border: "border-red-500" },
                { value: "delisted", label: `Delisted (${delistedCount})`, border: "border-orange-500" },
              ].map((t) => (
                <TabsTrigger key={t.value} value={t.value} className={`rounded-none border-b-2 border-transparent data-[state=active]:${t.border} data-[state=active]:bg-transparent px-6 py-3`}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {filteredBusinesses.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            {searchTerm ? "No businesses found matching your search." : "No businesses in this category."}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Business Name</TableHead>
                <TableHead className="font-semibold">Email</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Subscription</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Last Updated</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBusinesses.map((business) => (
                <TableRow key={business.id} className="hover:bg-slate-50">
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => handleEdit(business)}
                      className="text-left font-medium text-slate-900 hover:text-[#42D78A] transition-colors"
                      data-testid={`admin-edit-business-name-${business.id}`}
                    >
                      {business.business_name || "Unnamed"}
                    </button>
                  </TableCell>
                  <TableCell className="text-slate-600">{business.business_email}</TableCell>
                  <TableCell>
                    {business.business_type && <Badge variant="secondary" className="bg-slate-100 text-slate-700">{business.business_type}</Badge>}
                  </TableCell>
                  <TableCell>
                    {business.subscription_active ? (
                      <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                    ) : business.admin_subscription_override ? (
                      <Badge className="bg-blue-100 text-blue-800">Override</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700">No Sub</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <Badge className={business.status === "pending" ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100" : business.status === "rejected" ? "bg-red-100 text-red-800 hover:bg-red-100" : "bg-green-100 text-green-800 hover:bg-green-100"}>
                        {business.status === "pending" ? "Pending" : business.status === "rejected" ? "Rejected" : "Approved"}
                      </Badge>
                      {business.profile_review_needed && (
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                          <AlertTriangle className="mr-1 h-3 w-3" /> Edited
                        </Badge>
                      )}
                      {business.is_delisted && <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">Delisted</Badge>}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{formatLastUpdated(business.updated_at || business.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/business/${businessIdentifier(business)}`)} className="text-slate-600 hover:text-slate-900" data-testid={`admin-view-${business.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      {business.status === "pending" && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(business, "approved")} disabled={updatingStatus === business.id} className="text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`admin-approve-${business.id}`}>
                            {updatingStatus === business.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(business, "rejected")} disabled={updatingStatus === business.id} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`admin-reject-${business.id}`}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {business.status === "rejected" && (
                        <Button variant="ghost" size="sm" onClick={() => handleStatusChange(business, "approved")} disabled={updatingStatus === business.id} className="text-green-600 hover:text-green-700 hover:bg-green-50" data-testid={`admin-approve-${business.id}`}>
                          {updatingStatus === business.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                        </Button>
                      )}
                      {(business.status === "approved" || !business.status) && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelistToggle(business)} disabled={updatingStatus === business.id} className={business.is_delisted ? "text-green-600 hover:text-green-700 hover:bg-green-50" : "text-orange-500 hover:text-orange-700 hover:bg-orange-50"} title={business.is_delisted ? "Relist business" : "Delist business"} data-testid={`admin-delist-${business.id}`}>
                          {updatingStatus === business.id ? <Loader2 className="w-4 h-4 animate-spin" /> : business.is_delisted ? <RotateCcw className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                      )}
                      {(business.status === "approved" || !business.status) && !business.subscription_active && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleOverride(business)}
                          disabled={updatingStatus === business.id}
                          className={business.admin_subscription_override ? "text-blue-600 hover:bg-blue-50" : "text-amber-600 hover:bg-amber-50"}
                          title={business.admin_subscription_override ? "Remove subscription override" : "Override subscription requirement (force visible)"}
                          data-testid={`admin-toggle-override-business-${business.id}`}
                        >
                          {updatingStatus === business.id ? <Loader2 className="w-4 h-4 animate-spin" /> : business.admin_subscription_override ? <ShieldOff className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(business)} className="text-slate-600 hover:text-slate-900" data-testid={`admin-edit-${business.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleLoginAsBusiness(business)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" title="Login as business" data-testid={`admin-login-as-business-${business.id}`}>
                        <LogIn className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteModal({ open: true, business })} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`admin-delete-${business.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal.open} onOpenChange={(open) => !open && setDeleteModal({ open: false, business: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Delete Business</DialogTitle></DialogHeader>
          <p className="text-slate-600">Are you sure you want to delete <span className="font-semibold">{deleteModal.business?.business_name}</span>? This action cannot be undone.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteModal({ open: false, business: null })} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
